/* eslint-disable jsx-a11y/control-has-associated-label */

import React from 'react';
import { useEditorContext } from '@swell/easyblocks-editor';
import isObject from 'lodash/isObject';
import startCase from 'lodash/startCase';
import pt from 'prop-types';

import { Field } from 'components/form';
import StorefrontEditorIcon from 'components/storefront/editor/icon';
import { WidgetContext } from 'client/src/components/storefront/editor/widgets';

import SwellColorSchemePreview from './color-scheme-group/color-scheme-preview';
import SwellColorSchemePanel from './color-scheme-group/color-scheme-panel';
import withWidget from './with-widget';

function SwellColorScheme({ value, field, onChange }) {
  const [selected, setSelected] = React.useState('');
  const { form } = useEditorContext();
  const { editorConfig } = React.useContext(WidgetContext);

  const colorSchemeSetting = form.values.color_schemes.value;
  const fields = React.useMemo(() => {
    const colorSettings =
      editorConfig?.globals?.configs?.editor?.settings?.find((setting) =>
        setting.fields.some((field) => field.id === 'color_schemes'),
      );

    return colorSettings?.fields?.[0]?.fields || [];
  }, [editorConfig]);

  const colorSchemeOptions = React.useMemo(() => {
    if (!isObject(colorSchemeSetting)) {
      return [];
    }

    return Object.entries(colorSchemeSetting).map(([key, value]) => ({
      label: value?.settings?.label || startCase(key),
      value: key,
      settings: value?.settings,
    }));
  }, [colorSchemeSetting]);

  const onSchemeClick = React.useCallback(
    /** @param {React.MouseEvent} event */
    (event) => {
      setSelected(event.currentTarget.dataset.id);
    },
    [],
  );

  const currentScheme =
    colorSchemeSetting && Object.hasOwn(colorSchemeSetting, value)
      ? colorSchemeSetting[value]
      : null;

  const label = currentScheme ? (
    <div>
      <label
        className="storefront-editor--color-scheme--select--label"
        htmlFor={`form_field_${field.prop}`}
      >
        {field.label}
      </label>

      <div>
        <SwellColorSchemePreview
          schemeId={value}
          settings={currentScheme.settings}
          className="storefront-editor--color-scheme--preview-select"
          onSchemeClick={onSchemeClick}
        />
      </div>
    </div>
  ) : (
    field.label
  );

  const onColorSchemesChange = React.useCallback(
    (value) => {
      form.finalForm.change('color_schemes', {
        value,
        widgetId: 'SwellColorSchemeGroup',
      });
    },
    [form],
  );

  const onSchemeChange = React.useCallback(
    (schemeId, data) => {
      const updatedValue = {
        ...colorSchemeSetting,
        [schemeId]: {
          ...colorSchemeSetting?.[schemeId],
          settings: {
            ...colorSchemeSetting?.[schemeId]?.settings,
            ...data,
          },
        },
      };

      onColorSchemesChange(updatedValue);
    },
    [colorSchemeSetting, onColorSchemesChange],
  );

  const onSchemeValueChange = React.useCallback(
    (data) => {
      onSchemeChange(selected, data);
    },
    [selected, onSchemeChange],
  );

  const onSchemeDelete = React.useCallback(
    (updatedValue) => {
      const nextScheme = colorSchemeOptions.find(
        (schemeOption) => schemeOption.value !== selected,
      );

      onColorSchemesChange(updatedValue);
      setSelected('');
      onChange(null, nextScheme?.value || null);
    },
    [selected, colorSchemeOptions, onColorSchemesChange, onChange],
  );

  const onSchemeDuplicate = React.useCallback(
    (schemeId, updatedValue) => {
      onColorSchemesChange(updatedValue);
      setSelected(schemeId);
      onChange(null, schemeId);
    },
    [onColorSchemesChange, onChange],
  );

  const onPanelClose = React.useCallback(() => {
    setSelected('');
  }, []);

  return (
    <div className="storefront-editor-widget storefront-editor--color-scheme--select--wrapper">
      <div>{label}</div>

      <div className="storefront-editor--color-scheme--select">
        <Field
          type="select"
          name={field.prop}
          hint={field.description}
          value={value}
          options={colorSchemeOptions}
          ComponentOption={SwellColorSchemeOptionMemo}
          onChange={onChange}
        />
      </div>

      <SwellColorSchemePanel
        selected={selected}
        value={colorSchemeSetting}
        fields={fields}
        onSchemeChange={onSchemeValueChange}
        onSchemeDuplicate={onSchemeDuplicate}
        onSchemeDelete={onSchemeDelete}
        onPanelClose={onPanelClose}
      />
    </div>
  );
}

SwellColorScheme.propTypes = {
  value: pt.string,
  field: pt.object,
  params: pt.object,

  onChange: pt.func,
};

function SwellColorSchemeOption({ settings, label, current }) {
  return (
    <div className="storefront-editor--color-scheme--select--option">
      <div
        className="storefront-editor--color-scheme--preview-compact"
        style={{ color: settings.text, backgroundColor: settings.background }}
      >
        <div className="storefront-editor--color-scheme--preview-compact--text">
          Aa
        </div>

        <div className="storefront-editor--color-scheme--preview-compact--flex">
          <div
            className="storefront-editor--color-scheme--preview-compact--button"
            style={{
              color: settings.button_label,
              backgroundColor: settings.button,
              borderColor: settings.button,
            }}
          />

          <div
            className="storefront-editor--color-scheme--preview-compact--button"
            style={{
              color: settings.secondary_button_label,
              backgroundColor: 'transparent',
              borderColor: settings.secondary_button_label,
            }}
          />
        </div>
      </div>

      <div>{label}</div>

      {current && (
        <StorefrontEditorIcon
          className="storefront-editor--color-scheme--select--option--check"
          type="check"
        />
      )}
    </div>
  );
}

SwellColorSchemeOption.propTypes = {
  label: pt.string.isRequired,
  current: pt.bool,
  settings: pt.object.isRequired,
};

const SwellColorSchemeOptionMemo = React.memo(SwellColorSchemeOption);

export default React.memo(withWidget(SwellColorScheme));
